import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ModuleNavBar } from "../components/ModuleNavBar";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { ShieldCheck, Trash, SignOut, Key, UsersThree } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";

function formatDateTime(iso, language) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(language === "zh" ? "zh-CN" : "en-US", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminUsersPage() {
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.is_admin) loadUsers();
  }, [currentUser]);

  if (currentUser && !currentUser.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleToggleActive = async (targetUser) => {
    try {
      const { data } = await api.patch(`/admin/users/${targetUser.id}/status`, { is_active: !targetUser.is_active });
      setUsers((prev) => prev.map((u) => (u.id === targetUser.id ? data : u)));
      toast.success(data.is_active ? t("admin.userEnabledSuccess") : t("admin.userDisabledSuccess"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleForceLogout = async (targetUser) => {
    try {
      await api.post(`/admin/users/${targetUser.id}/force-logout`);
      toast.success(t("admin.forceLogoutSuccess"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDelete = async (targetUser) => {
    try {
      await api.delete(`/admin/users/${targetUser.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== targetUser.id));
      toast.success(t("admin.deleteUserSuccess"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.trim().length < 6) {
      toast.error(t("admin.passwordTooShortError"));
      return;
    }
    setResetSubmitting(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: newPassword.trim() });
      toast.success(t("admin.resetPasswordSuccess"));
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="admin-users-page">
      <ModuleNavBar active="" />
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-5xl mx-auto w-full">
          <h1 className="font-display text-xl md:text-2xl font-black tracking-tight flex items-center gap-2 mb-4">
            <UsersThree size={20} /> {t("admin.pageTitle")}
          </h1>

          {loading ? (
            <p className="text-sm font-bold text-muted-foreground" data-testid="admin-users-loading">{t("common.loading")}</p>
          ) : (
            <div className="border border-border bg-card" data-testid="admin-users-table-wrapper">
              <Table data-testid="admin-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.colEmail")}</TableHead>
                    <TableHead>{t("admin.colName")}</TableHead>
                    <TableHead>{t("admin.colRegistered")}</TableHead>
                    <TableHead>{t("admin.colLastLogin")}</TableHead>
                    <TableHead>{t("admin.colStatus")}</TableHead>
                    <TableHead>{t("admin.colRole")}</TableHead>
                    <TableHead className="text-right">{t("admin.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                        <TableCell className="font-semibold text-sm" data-testid={`admin-user-email-${u.id}`}>{u.email}</TableCell>
                        <TableCell className="text-sm">{u.name || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDateTime(u.created_at, i18n.language)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground" data-testid={`admin-user-last-login-${u.id}`}>{formatDateTime(u.last_login_at, i18n.language)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              data-testid={`admin-user-active-toggle-${u.id}`}
                              checked={u.is_active}
                              disabled={isSelf}
                              onCheckedChange={() => handleToggleActive(u)}
                            />
                            <Badge
                              data-testid={`admin-user-status-badge-${u.id}`}
                              variant={u.is_active ? "default" : "destructive"}
                              className="rounded-none"
                            >
                              {u.is_active ? t("admin.statusActive") : t("admin.statusDisabled")}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.is_admin ? (
                            <Badge data-testid={`admin-user-role-badge-${u.id}`} className="rounded-none gap-1">
                              <ShieldCheck size={12} weight="fill" /> {t("admin.roleAdmin")}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t("admin.roleUser")}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              data-testid={`admin-user-reset-password-${u.id}`}
                              variant="outline"
                              size="icon"
                              title={t("admin.resetPasswordAction")}
                              className="rounded-none h-8 w-8"
                              onClick={() => { setResetTarget(u); setNewPassword(""); }}
                            >
                              <Key size={14} />
                            </Button>
                            <Button
                              data-testid={`admin-user-force-logout-${u.id}`}
                              variant="outline"
                              size="icon"
                              title={t("admin.forceLogoutAction")}
                              className="rounded-none h-8 w-8"
                              onClick={() => handleForceLogout(u)}
                            >
                              <SignOut size={14} />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  data-testid={`admin-user-delete-${u.id}`}
                                  variant="outline"
                                  size="icon"
                                  title={t("admin.deleteUserAction")}
                                  disabled={isSelf}
                                  className="rounded-none h-8 w-8 text-destructive hover:bg-destructive/10"
                                >
                                  <Trash size={14} />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="rounded-none" data-testid={`admin-user-delete-confirm-${u.id}`}>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{t("admin.deleteConfirmTitle")}</AlertDialogTitle>
                                  <AlertDialogDescription>{t("admin.deleteConfirmDescription", { email: u.email })}</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid={`admin-user-delete-cancel-${u.id}`} className="rounded-none">{t("common.cancel")}</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`admin-user-delete-confirm-button-${u.id}`}
                                    className="rounded-none bg-destructive hover:bg-destructive/90"
                                    onClick={() => handleDelete(u)}
                                  >
                                    {t("common.delete")}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPassword(""); } }}>
        <DialogContent className="rounded-none max-w-sm" data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("admin.resetPasswordTitle")}</DialogTitle>
            <DialogDescription>{t("admin.resetPasswordDescription", { email: resetTarget?.email })}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
            <Input
              data-testid="admin-reset-password-input"
              type="password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t("admin.newPasswordPlaceholder")}
              className="rounded-none font-bold text-sm"
            />
            <DialogFooter>
              <Button
                type="submit"
                data-testid="admin-reset-password-submit-button"
                disabled={resetSubmitting}
                className="rounded-none w-full font-bold"
              >
                {resetSubmitting ? t("common.saving") : t("admin.resetPasswordSubmit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
